import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const mockPost = {
  id: '1',
  title: 'SEO for Headless E-commerce: What B2B and B2C Brands Get Wrong',
  slug: 'seo-headless-ecommerce-b2b-b2c',
  excerpt: 'Migrating to a headless commerce platform can wreck your SEO if you\'re not careful. Here\'s what actually matters for React and Next.js storefronts.',
  content: `
    <h2>Introduction</h2>
    <p>Headless commerce gives brands more control over the customer experience — faster storefronts, flexible frontends, and freedom from rigid platform templates. But it also introduces a real risk: if the migration isn't handled correctly, search rankings built over years can quietly disappear within weeks of launch.</p>
    <p>We've led migrations from legacy platforms to headless HCL Commerce and Next.js frontends, and the SEO issues that show up are almost always the same handful of mistakes. Here's what to watch for.</p>

    <h2>1. Server-Side Rendering Is Not Optional</h2>
    <p>A common mistake is shipping a client-side-rendered React storefront and assuming search engines will crawl it the same way they crawl server-rendered pages. In practice, JavaScript-heavy pages that rely entirely on client-side rendering are crawled and indexed less reliably, and often more slowly, than pages with content available on first load.</p>
    <p>For any storefront built on React or Next.js, server-side rendering (or static generation for pages that don't change often, like category pages) should be the default, not an afterthought bolted on later.</p>

    <h2>2. URL Structure Changes Are the Silent Killer</h2>
    <p>Migrating platforms almost always changes URL patterns — a legacy JSP-based storefront and a headless React or Next.js frontend rarely produce identical paths. Every changed URL without a proper 301 redirect is a page that loses its accumulated ranking signal and starts from zero.</p>
    <p>Before any migration, map every existing indexed URL to its new destination. This is tedious, unglamorous work — and it's the single highest-leverage SEO task in any platform migration.</p>

    <h2>3. Product and Category Metadata Needs to Survive the Migration</h2>
    <p>Titles, meta descriptions, structured data (schema.org markup for products, pricing, and reviews), and canonical tags are easy to lose when content moves from a monolithic platform's templating system into a new headless frontend. Audit what exists before migration, and verify it's rebuilt — not just visually, but in the page's actual HTML output — after launch.</p>

    <h2>4. B2B and B2C Storefronts Have Different SEO Priorities</h2>
    <p>A B2C storefront usually optimizes for high-intent product and category pages reachable by anonymous search traffic. A B2B storefront often has a different profile — gated pricing, account-specific catalogs, and content that search engines may never see at all behind a login.</p>
    <p>When unifying B2B and B2C onto a single platform, it's worth deciding deliberately which parts of the catalog should remain public and crawlable, and which are appropriately gated. Getting this wrong either exposes account-specific data publicly, or accidentally blocks search engines from your public product catalog.</p>

    <h2>5. Page Speed Gains Only Count If They're Measured</h2>
    <p>Headless migrations are often pitched partly on performance improvements — and legitimately, a well-built headless frontend usually is faster than an older monolithic platform. But "faster" needs to be verified with real Core Web Vitals data before and after launch, not assumed. Regressions can hide inside a migration just as easily as improvements can.</p>

    <h2>Conclusion</h2>
    <p>Headless commerce is a real upgrade when it's done right — but the SEO risk in a platform migration is almost entirely preventable with the right discovery and redirect mapping upfront. If you're planning a migration off a legacy platform, treat SEO continuity as a launch requirement, not a post-launch fix.</p>
  `,
  author: 'FuturePath Technologies',
  category: 'E-commerce & SEO',
  published_date: '2026-08-04',
  featured: true,
  image_url: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=1200',
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = mockPost;

  return (
    <>
      <Helmet>
        <title>{`${post.title}- FuturePath Technologies Blog`}</title>
        <meta name="description" content={post.excerpt} />
      </Helmet>

      <section className="relative pt-32 pb-12">
        <div className="container-custom max-w-4xl">
          <Button
            asChild
            variant="ghost"
            className="mb-8 text-muted-foreground hover:text-white"
            data-testid="back-to-blog-btn"
          >
            <Link to="/blog">
              <ArrowLeft className="mr-2" size={20} />
              Back to Blog
            </Link>
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
              <span className="text-primary font-mono">{post.category}</span>
              <span className="flex items-center gap-1">
                <Calendar size={16} />
                {new Date(post.published_date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              <span className="flex items-center gap-1">
                <User size={16} />
                {post.author}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold font-outfit mb-6 text-white" data-testid="post-title">
              {post.title}
            </h1>

            <img
              src={post.image_url}
              alt={post.title}
              className="w-full rounded-3xl glass-card p-2 mb-8"
            />

            <div
              className="prose prose-invert prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content }}
              data-testid="post-content"
              style={{
                color: 'rgb(var(--foreground))',
              }}
            />
          </motion.div>
        </div>
      </section>
    </>
  );
}
